import fetchData from "../fetcher"
import type { SocialCallbackRequest, SocialInitiateResponse } from "./types"

const SSO_REDIRECT_URI_KEY = "sso_redirect_uri"

/** App callback for a social provider. Port is part of the registered URI. */
export const getSocialRedirectUri = (provider: string): string =>
  `${window.location.origin}/sso/${provider}/callback`

export const socialRedirectTarget = (response: SocialInitiateResponse): string | undefined =>
  response.redirect_uri ||
  response.redirectUri ||
  response.authorizationUrl ||
  response.url ||
  response.redirectUrl ||
  response.providerUrl

/**
 * `GET /iam/v4/auth/social/initiate` — Blocks OS social login start
 * (`@seliseblocks/client` `auth.socialInitiate`).
 */
export const socialInitiate = async (
  clientId: string,
  redirectUri: string
): Promise<SocialInitiateResponse> => {
  const searchParams = new URLSearchParams({ clientId, redirectUri })
  return fetchData<SocialInitiateResponse>(
    `/iam/v4/auth/social/initiate?${searchParams.toString()}`,
    { method: "GET" }
  )
}

export const startSocialLogin = async (clientId: string, provider: string): Promise<void> => {
  const redirectUri = getSocialRedirectUri(provider)
  sessionStorage.setItem(SSO_REDIRECT_URI_KEY, redirectUri)

  const response = await socialInitiate(clientId, redirectUri)
  const targetUrl = socialRedirectTarget(response)
  if (!targetUrl) {
    throw new Error("Social login initiate did not return a redirect URL")
  }
  window.location.assign(targetUrl)
}

export const consumeSocialRedirectUri = (): string | undefined => {
  const stored = sessionStorage.getItem(SSO_REDIRECT_URI_KEY)
  if (stored) sessionStorage.removeItem(SSO_REDIRECT_URI_KEY)
  return stored ?? undefined
}

/**
 * `POST /iam/v4/auth/social/callback` — Blocks OS social login completion
 * (`@seliseblocks/client` `auth.socialCallback`).
 */
export const socialCallback = async <TResponse = unknown>(
  request: SocialCallbackRequest
): Promise<TResponse> => {
  return fetchData<TResponse>("/iam/v4/auth/social/callback", {
    method: "POST",
    body: request,
  })
}
